import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, campagneApi, fornitoriApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Radio, 
  Users, 
  Euro, 
  Megaphone,
  TrendingUp,
  ArrowRight,
  RefreshCw,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#4F46E5', '#F43F5E', '#10B981', '#F59E0B'];

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentCampagne, setRecentCampagne] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, campagneRes] = await Promise.all([
        dashboardApi.getStats(),
        campagneApi.getAll()
      ]);
      setStats(statsRes.data);
      setRecentCampagne(campagneRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const spotChartData = stats ? [
    { name: '1 min', value: stats.spot_per_durata['1min'], fill: COLORS[0] },
    { name: '30 sec', value: stats.spot_per_durata['30sec'], fill: COLORS[1] },
    { name: '20 sec', value: stats.spot_per_durata['20sec'], fill: COLORS[2] },
    { name: '15 sec', value: stats.spot_per_durata['15sec'], fill: COLORS[3] },
  ] : [];

  const formatCurrency = (value) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

  const getStatoBadge = (stato) => {
    const variants = { bozza: 'bg-slate-100 text-slate-700', confermata: 'bg-green-100 text-green-700', completata: 'bg-blue-100 text-blue-700' };
    return <Badge className={variants[stato] || variants.bozza}>{stato}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div><div className="h-8 bg-slate-200 rounded w-3/4"></div></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Panoramica delle tue campagne pubblicitarie</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-dashboard-btn">
          <RefreshCw size={16} className="mr-2" />Aggiorna
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="stats-card" data-testid="stat-fornitori">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Fornitori (Radio)</p>
                <p className="text-3xl font-bold font-['Manrope'] mt-1">{stats?.totale_fornitori || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Radio className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stat-clienti">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Clienti</p>
                <p className="text-3xl font-bold font-['Manrope'] mt-1">{stats?.totale_clienti || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-rose-500/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-rose-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stat-campagne">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Campagne</p>
                <p className="text-3xl font-bold font-['Manrope'] mt-1">{stats?.totale_campagne || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Megaphone className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stat-margine">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Margine Totale</p>
                <p className={`text-2xl font-bold font-['Manrope'] mt-1 ${(stats?.margine_totale || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(stats?.margine_totale || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <Card className="stats-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Spot Disponibili</p>
                <p className="text-3xl font-bold font-['Manrope'] mt-1">{stats?.totale_spot_disponibili || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Valore Inventario</p>
                <p className="text-2xl font-bold font-['Manrope'] mt-1">{formatCurrency(stats?.valore_inventario || 0)}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                <Euro className="h-6 w-6 text-violet-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <Card className="lg:col-span-8" data-testid="chart-spot-durata">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Clock size={20} className="text-primary" />
              Spot Disponibili per Durata
            </CardTitle>
          </CardHeader>
          <CardContent>
            {spotChartData.some(d => d.value > 0) ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={spotChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }} formatter={(value) => [value, 'Spot']} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {spotChartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                <p>Nessun dato disponibile</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-4" data-testid="chart-distribuzione">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Distribuzione</CardTitle>
          </CardHeader>
          <CardContent>
            {spotChartData.some(d => d.value > 0) ? (
              <>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={spotChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                        {spotChartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  {spotChartData.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                      <span className="text-sm text-slate-600">{item.name}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <p>Nessun dato</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Campagne & Top Emittenti */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="recent-campagne">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Manrope']">Campagne Recenti</CardTitle>
            <Link to="/campagne">
              <Button variant="ghost" size="sm">Vedi tutte<ArrowRight size={16} className="ml-2" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentCampagne.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Megaphone size={40} className="mx-auto mb-3 text-slate-300" />
                <p>Nessuna campagna</p>
                <Link to="/campagne"><Button variant="link" className="mt-2">Crea la prima campagna</Button></Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentCampagne.map((campagna) => (
                  <Link key={campagna.id} to={`/campagne/${campagna.id}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{campagna.numero_campagna}</p>
                        {getStatoBadge(campagna.stato)}
                      </div>
                      <p className="text-sm text-slate-500">{campagna.cliente_denominazione}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatCurrency(campagna.valore_campagna)}</p>
                      <p className={`text-sm ${campagna.margine >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Margine: {formatCurrency(campagna.margine)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="top-emittenti">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Top Emittenti</CardTitle>
          </CardHeader>
          <CardContent>
            {!stats?.top_emittenti?.length ? (
              <div className="text-center py-8 text-slate-500">
                <Radio size={40} className="mx-auto mb-3 text-slate-300" />
                <p>Nessuna emittente configurata</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.top_emittenti.map((emittente, index) => (
                  <div key={emittente.nome} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-primary' : index === 1 ? 'bg-rose-500' : 'bg-slate-400'}`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{emittente.nome}</p>
                        <p className="text-sm text-slate-500">{emittente.spot_totali} spot</p>
                      </div>
                    </div>
                    <p className="font-semibold text-primary">{formatCurrency(emittente.valore)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
